import { useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useIoContext, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { QueryKeys } from '../queryKeys';
import { useQueryState } from '../useQueryState';
import { buildDiffPayload, diffActivityLogs } from './useActivityLog.helpers';
const EMPTY_ACTIVITY_LOG = [];
const fetchSingle = (api, relationshipId, ffOpen)=>ffOpen ? api.activityLog.get(relationshipId) : api.activityLog.getOld(relationshipId);
const fetchAndCompare = async (api, relationshipId, ffOpen, getNow)=>{
    const [oldOutcome, newOutcome] = await Promise.allSettled([
        api.activityLog.getOld(relationshipId),
        api.activityLog.get(relationshipId)
    ]);
    if (oldOutcome.status === 'fulfilled' && newOutcome.status === 'fulfilled') {
        const diff = diffActivityLogs(oldOutcome.value, newOutcome.value);
        if (diff != null) {
            api.activityLog.postDiff(buildDiffPayload({
                relationshipId,
                ffOpen,
                diff,
                oldResult: oldOutcome.value,
                newResult: newOutcome.value,
                timestamp: getNow().toISOString()
            })).catch(()=>undefined);
        }
    } else {
        if (oldOutcome.status === 'rejected') {
            Sentry.captureException(oldOutcome.reason, {
                extra: {
                    hook: 'useActivityLog',
                    side: 'old',
                    relationshipId
                }
            });
        }
        if (newOutcome.status === 'rejected') {
            Sentry.captureException(newOutcome.reason, {
                extra: {
                    hook: 'useActivityLog',
                    side: 'new',
                    relationshipId
                }
            });
        }
    }
    const selectedOutcome = ffOpen ? newOutcome : oldOutcome;
    if (selectedOutcome.status === 'rejected') {
        throw selectedOutcome.reason;
    }
    return selectedOutcome.value;
};
const applyNotificationFilters = (entries, surface, notificationsEnabled)=>{
    if (surface !== 'minisite' || !notificationsEnabled) {
        return entries.filter((entry)=>!entry.isSentNotificationEntry());
    }
    return entries;
};
export const useActivityLog = (relationshipId, param)=>{
    let { surface, refreshOnHookMount = false } = param;
    const { api } = useStoreActions();
    const { getNow } = useIoContext();
    const { ACTIVITY_LOG_V2, ACTIVITY_LOG_DUAL_CALL, ACTIVITY_LOG_NOTIFICATION_ENTRIES } = useFeatureFlags();
    const notificationsEnabled = !!ACTIVITY_LOG_NOTIFICATION_ENTRIES;
    const ffOpen = !!ACTIVITY_LOG_V2 || notificationsEnabled;
    const dualCallEnabled = !!ACTIVITY_LOG_DUAL_CALL;
    const { data, isLoading, error, refresh } = useQueryState({
        queryKey: [
            QueryKeys.ActivityLog,
            relationshipId,
            ffOpen,
            dualCallEnabled
        ],
        queryFn: ()=>dualCallEnabled ? fetchAndCompare(api, relationshipId, ffOpen, getNow) : fetchSingle(api, relationshipId, ffOpen),
        enabled: !!relationshipId,
        refreshOnHookMount
    });
    const filteredActivityLog = useMemo(()=>data ? applyNotificationFilters(data, surface, notificationsEnabled) : EMPTY_ACTIVITY_LOG, [
        data,
        surface,
        notificationsEnabled
    ]);
    return {
        activityLog: filteredActivityLog,
        isLoading,
        error,
        refresh
    };
};
